import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import useCanDelete from "@/lib/useCanDelete";
import EmailDeliveryLog from "@/components/EmailDeliveryLog";
import InlineShortcutAssigner from "@/components/InlineShortcutAssigner";
import WelcomeOverlay from "@/components/WelcomeOverlay";
import { Plus, PencilSimple, Trash, UserPlus, UserMinus, Copy, DeviceMobile, CheckCircle, DotsThreeVertical, WhatsappLogo, UploadSimple, Download, Lock, UsersThree } from "@phosphor-icons/react";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import ManageAgentsDialog from "@/components/ManageAgentsDialog";
import PageShortcutHeader from "@/components/PageShortcutHeader";

const emptyParty = {
    name: "", contact_person: "", phone: "", email: "", address: "",
    gst_no: "", credit_limit: 0, // kept for backend compatibility, hidden from UI
    opening_balance: 0, active: true,
    // v1.27.30 — Per-party email schedule for daily ledger delivery.
    // Default fire time is 23:45 IST (end-of-day digest with today's progress).
    email_schedule: {
        frequency: "off",           // off | daily | weekly | monthly
        time_hhmm: "23:45",
        weekday: 1,                 // Monday
        day_of_month: 1,
        recipient: "",              // falls back to party.email when blank
    },
};

const emptyUser = { email: "", password: "", name: "", phone: "", role: "customer", party_id: "" };

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const PORTAL_URL = `${window.location.origin}/login`;

export default function Parties() {
    const canDelete = useCanDelete();
    const navigate = useNavigate();
    const { user: authUser } = useAuth();
    // v1.22.0 — Only super admin OR admins with the create-customer-logins
    // permission can create portal logins. Others see the option greyed out.
    const canCreateLogins = !!authUser?.is_super_admin || !!authUser?.admin_permissions?.can_create_customer_logins;
    const [parties, setParties] = useState([]);
    const [users, setUsers] = useState([]);
    const [open, setOpen] = useState(false);
    const [welcomeParty, setWelcomeParty] = useState(null); // v1.25.48 — welcome overlay
    const [userOpen, setUserOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyParty);
    const [userForm, setUserForm] = useState(emptyUser);
    const [shareInfo, setShareInfo] = useState(null); // { partyName, email, password }
    const [importOpen, setImportOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [importBusy, setImportBusy] = useState(false);
    const [savingParty, setSavingParty] = useState(false);
    const [agentsForParty, setAgentsForParty] = useState(null); // v1.27.90 — Manage Agents dialog
    const [bulkEmailBusy, setBulkEmailBusy] = useState(false);
    // v1.27.31 — Fix Emails modal state
    const [fixOpen, setFixOpen] = useState(false);
    const [fixHealth, setFixHealth] = useState(null); // { counts, parties }
    const [fixDraft, setFixDraft] = useState({}); // { [partyId]: emailStr }
    const [fixBusy, setFixBusy] = useState(false);
    const [healthCounts, setHealthCounts] = useState(null);

    const loadEmailHealth = async () => {
        try {
            const { data } = await api.get("/parties-email-health");
            setHealthCounts(data.counts);
            return data;
        } catch (e) { toast.error(formatApiError(e)); return null; }
    };

    const openFixEmails = async () => {
        const data = await loadEmailHealth();
        if (!data) return;
        setFixHealth(data);
        const draft = {};
        (data.parties || []).forEach((p) => {
            if (p.status !== "ok") draft[p.id] = p.party_email || "";
        });
        setFixDraft(draft);
        setFixOpen(true);
    };

    const saveFixEmails = async () => {
        setFixBusy(true);
        try {
            const rows = Object.entries(fixDraft)
                .map(([id, email]) => ({ id, email: (email || "").trim() }));
            const { data } = await api.post("/parties-fix-emails", { rows });
            const parts = [`${data.updated} updated`];
            if (data.invalid?.length) parts.push(`${data.invalid.length} rejected as invalid`);
            toast.success(parts.join(" · "));
            setFixOpen(false);
            await load();
            await loadEmailHealth();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setFixBusy(false); }
    };

    // v1.27.31 — refresh email-health counts on mount so badge shows accurate count.
    useEffect(() => { loadEmailHealth(); }, []);

    // v1.27.30 — Bulk toggle: turn the daily 23:45 IST ledger email ON or OFF
    // for every active party in one click. Existing weekly/monthly per-party
    // overrides are preserved when enabling.
    const bulkEmailSchedule = async (enabled) => {
        const verb = enabled ? "TURN ON" : "TURN OFF";
        if (!window.confirm(`${verb} the daily 23:45 IST ledger email for EVERY active party?\n\nEach party will receive their ledger PDF + today's day-progress by email every night at 23:45 IST.\n\nYou can still edit individual parties afterwards.`)) return;
        setBulkEmailBusy(true);
        try {
            const { data } = await api.post("/parties-bulk-email-schedule", { enabled, time_hhmm: "23:45" });
            toast.success(`${data.changed}/${data.total} parties updated · daily email ${enabled ? "ON @ 23:45 IST" : "OFF"}`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBulkEmailBusy(false); }
    };
    const [savingUser, setSavingUser] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [cleanupBusy, setCleanupBusy] = useState(false);

    const cleanupOrphans = async () => {
        if (cleanupBusy) return;
        if (!window.confirm("Scan for portal logins whose party no longer exists and delete them?\n\nThis fixes the 'Mobile/Email already registered' error caused by legacy orphan rows. Active party logins are untouched.")) return;
        setCleanupBusy(true);
        try {
            const { data } = await api.post("/admin/cleanup-orphan-users");
            if (data.removed > 0) {
                toast.success(`Cleaned up ${data.removed} orphan portal login${data.removed > 1 ? "s" : ""}`);
                await load();
            } else {
                toast.success("No orphan logins found — you're clean.");
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setCleanupBusy(false); }
    };

    const importCsv = async () => {
        if (!importFile) return;
        setImportBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", importFile);
            const { data } = await api.post("/parties/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setImportResult(data);
            const c = data.created?.length || 0;
            toast.success(`${c} parties imported`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setImportBusy(false); }
    };

    const downloadTemplate = () => {
        const csv = "name,contact_person,phone,email,address,gst_no,opening_balance,notes,active\n" +
                    "Example Traders,Rahul Kumar,9876543210,rahul@example.com,\"Mumbai, MH\",27AAAA0000A1Z5,15000,VIP customer,true\n" +
                    "Sample Impex,,9998887776,,,,0,,true\n";
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "parties-template.csv"; a.click();
        URL.revokeObjectURL(url);
    };

    const load = async () => {
        const [p, u] = await Promise.all([api.get("/parties"), api.get("/users").catch(() => ({ data: [] }))]);
        setParties(p.data);
        setUsers(u.data);
    };
    useEffect(() => { load(); }, []);

    const userForParty = (pid) => users.find((u) => u.role === "customer" && u.party_id === pid);

    const openCreate = () => { setEditing(null); setForm(emptyParty); setOpen(true); };
    const openEdit = (p) => { setEditing(p); setForm({ ...emptyParty, ...p }); setOpen(true); };

    const save = async (e) => {
        e.preventDefault();
        if (savingParty) return; // Guard against double-click / rapid re-submit
        setSavingParty(true);
        try {
            const body = { ...form, opening_balance: Number(form.opening_balance) };
            let created;
            if (editing) {
                const r = await api.put(`/parties/${editing.id}`, body);
                created = r.data;
                toast.success("Party updated");
                setOpen(false);
                load();
            } else {
                const r = await api.post("/parties", body);
                created = r.data;
                toast.success("Party created — now create their portal login");
                setOpen(false);
                await load();
                setWelcomeParty(created); // v1.25.48 — celebration overlay
                // Auto-open portal login dialog for the new party
                openAddUser(created);
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingParty(false); }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete party ${p.name}?\n\nThis will also remove their portal login and any pending notifications. The ledger history stays.`)) return;
        if (deletingId) return;
        setDeletingId(p.id);
        try {
            await api.delete(`/parties/${p.id}`);
            toast.success("Party deleted");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDeletingId(null); }
    };

    const removeUser = async (p) => {
        const u = userForParty(p.id);
        if (!u) return;
        const label = u.email || u.phone || u.name;
        if (!window.confirm(`Remove portal login for ${p.name}?\n\n${label}\n\nThe party's ledger and payment slips will be kept, but they will no longer be able to sign in until you create a new login.`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            toast.success("Portal login removed");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const openAddUser = (p) => {
        setUserForm({
            ...emptyUser,
            party_id: p.id,
            email: p.email || "",
            phone: (p.phone || "").replace(/\D/g, "").slice(-10),
            name: p.contact_person || p.name || "",
        });
        setUserOpen(true);
    };

    const saveUser = async (e) => {
        e.preventDefault();
        if (savingUser) return;
        const emailTrim = (userForm.email || "").trim();
        const phoneNorm = (userForm.phone || "").replace(/\D/g, "").slice(-10);
        if (!emailTrim && !phoneNorm) {
            toast.error("Provide at least an email or a 10-digit mobile number");
            return;
        }
        if (phoneNorm && phoneNorm.length !== 10) {
            toast.error("Mobile must be 10 digits");
            return;
        }
        setSavingUser(true);
        try {
            const payload = { ...userForm, email: emailTrim, phone: phoneNorm };
            await api.post("/users", payload);
            const party = parties.find((p) => p.id === userForm.party_id);
            setShareInfo({
                partyName: party?.name || userForm.name,
                partyPhone: party?.phone || "",
                email: emailTrim,
                phone: phoneNorm,
                password: userForm.password,
            });
            setUserOpen(false);
            setShareOpen(true);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingUser(false); }
    };

    const copyText = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Copied");
        } catch {
            toast.error("Could not copy — please select and copy manually");
        }
    };

    const buildShareMessage = () => {
        if (!shareInfo) return "";
        let loginLine;
        if (shareInfo.email && shareInfo.phone) {
            loginLine = `Login: ${shareInfo.email} (or mobile ${shareInfo.phone})`;
        } else if (shareInfo.email) {
            loginLine = `Login: ${shareInfo.email}`;
        } else {
            loginLine = `Login (mobile): ${shareInfo.phone}`;
        }
        return `Hi ${shareInfo.partyName},\n\nAccess your MICM customer portal:\n\nURL: ${PORTAL_URL}\n${loginLine}\nPassword: ${shareInfo.password}\n\nOpen the link on your phone browser, sign in, and use "Upload Slip" to send us your payment screenshots.`;
    };

    const copyAll = () => {
        if (!shareInfo) return;
        copyText(buildShareMessage());
    };

    const buildWhatsAppMessage = () => buildShareMessage();

    const normalizePhone = (raw) => {
        if (!raw) return "";
        const digits = raw.replace(/\D/g, "");
        // If already 12+ (has country code) leave as-is; else assume India (+91) for 10-digit numbers
        if (digits.length === 10) return `91${digits}`;
        return digits;
    };

    const openWhatsApp = () => {
        const text = encodeURIComponent(buildWhatsAppMessage());
        const phone = normalizePhone(shareInfo?.partyPhone);
        const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <div className="space-y-6" data-testid="parties-page">
            <WelcomeOverlay
                open={!!welcomeParty}
                onClose={() => setWelcomeParty(null)}
                title="Party Added!"
                subtitle={welcomeParty ? `${welcomeParty.name} is now on your ledger — set up their portal login next.` : ""}
            />
            <div className="flex flex-wrap items-end justify-between gap-4">
                <PageShortcutHeader
                    action_id="page:parties"
                    label="Parties"
                    target_route="/admin/parties"
                    eyebrow="Master Data"
                    title="Parties"
                    titleClassName="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900"
                />
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        data-testid="fix-emails-btn"
                        onClick={openFixEmails}
                        variant="outline"
                        className={`${(healthCounts?.missing || 0) + (healthCounts?.invalid || 0) > 0 ? "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100" : "border-slate-300 text-slate-600 hover:bg-slate-100"} relative`}
                        title="Review parties with missing or invalid email addresses and fix them in one go"
                    >
                        <PencilSimple size={16} className="mr-1.5" />
                        Fix Emails
                        {(healthCounts?.missing || 0) + (healthCounts?.invalid || 0) > 0 && (
                            <span data-testid="fix-emails-badge" className="ml-1.5 inline-flex items-center rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                                {(healthCounts?.missing || 0) + (healthCounts?.invalid || 0)}
                            </span>
                        )}
                    </Button>
                    <Button
                        data-testid="bulk-email-on-btn"
                        onClick={bulkEmailSchedule.bind(null, true)}
                        disabled={bulkEmailBusy}
                        variant="outline"
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                        title="Turn ON daily 23:45 IST ledger email for every active party"
                    >
                        <CheckCircle size={16} className="mr-1.5" />
                        {bulkEmailBusy ? "Working…" : "Daily Email ON (all)"}
                    </Button>
                    <Button
                        data-testid="bulk-email-off-btn"
                        onClick={bulkEmailSchedule.bind(null, false)}
                        disabled={bulkEmailBusy}
                        variant="outline"
                        className="border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-60"
                        title="Turn OFF daily ledger email for every active party"
                    >
                        Daily Email OFF
                    </Button>
                    <Button
                        data-testid="cleanup-orphans-btn"
                        onClick={cleanupOrphans}
                        disabled={cleanupBusy}
                        variant="outline"
                        className="border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-60"
                        title="Delete portal logins whose party was removed but the login stayed behind"
                    >
                        <UserMinus size={16} className="mr-1.5" />
                        {cleanupBusy ? "Cleaning…" : "Fix orphan logins"}
                    </Button>
                    <Button
                        data-testid="import-parties-btn"
                        onClick={() => { setImportFile(null); setImportResult(null); setImportOpen(true); }}
                        variant="outline"
                        className="border-slate-300"
                    >
                        <UploadSimple size={16} className="mr-2" /> Import CSV
                    </Button>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <div className="flex items-center gap-1">
                            <Button data-testid="add-party-btn" onClick={openCreate} className="bg-blue-600 text-white hover:bg-blue-700">
                                <Plus size={16} className="mr-2" /> Add Party
                            </Button>
                            {/* v1.28.87 — Inline shortcut assigner (Super User only). */}
                            <InlineShortcutAssigner action_id="nav:add-party-btn" label="Add Party" target_route="/admin/parties?add=1" category="Party Management" />
                        </div>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0">
                        <DialogHeader className="border-b border-slate-100 px-6 pt-5 pb-3">
                            <DialogTitle className="font-display">{editing ? "Edit Party" : "New Party"}</DialogTitle>
                            <DialogDescription className="text-xs text-slate-500">Master record used across ledgers, uploads and reports.</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={save} className="flex flex-1 flex-col overflow-hidden" data-testid="party-form">
                            <div className="flex-1 overflow-y-auto px-6 py-4 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="party-form-body">
                                <Field label="Party Name *" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="party-name" />
                                <Field label="Contact Person" value={form.contact_person} onChange={(v) => setForm({ ...form, contact_person: v })} testId="party-contact" />
                                <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="party-phone" />
                                <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="party-email" />
                                <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="party-address" className="sm:col-span-2" />
                                {/* v1.23.0 — Per-party email schedule (spans full width) */}
                                <div className="sm:col-span-2">
                                    <EmailScheduleBlock
                                        value={form.email_schedule}
                                        partyEmail={form.email}
                                        onChange={(next) => setForm({ ...form, email_schedule: next })}
                                        editingId={editing?.id}
                                    />
                                </div>
                                {editing?.id && (
                                    // v1.28.85 — Cap the Delivery Log at 200px with its own internal
                                    // scroller. Previously the table grew unbounded and pushed the
                                    // Opening Balance / Update button off screen inside the modal.
                                    <div className="sm:col-span-2 max-h-[200px] overflow-y-auto rounded-lg border border-slate-200" data-testid="party-form-delivery-log-wrap">
                                        <EmailDeliveryLog partyId={editing.id} />
                                    </div>
                                )}
                                <Field type="number" label="Opening Balance (₹)" value={form.opening_balance} onChange={(v) => setForm({ ...form, opening_balance: v })} testId="party-opening" />
                                <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                                    <Switch data-testid="party-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                                    <Label className="text-sm">Active</Label>
                                </div>
                            </div>
                            {/* v1.28.85 — Pinned footer stays visible while the body scrolls. */}
                            <DialogFooter className="shrink-0 border-t border-slate-100 bg-white px-6 py-3">
                                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                                <Button
                                    type="submit"
                                    data-testid="save-party-btn"
                                    disabled={savingParty}
                                    className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {savingParty ? "Saving…" : (editing ? "Update" : "Create Party")}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            {/* v1.27.31 — Fix Emails Dialog. Lists every opted-in party whose
                recipient email is missing/invalid and lets the admin type in
                the correct email inline. One save fixes them all. */}
            <Dialog open={fixOpen} onOpenChange={setFixOpen}>
                <DialogContent className="max-w-2xl" data-testid="fix-emails-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Fix Party Emails</DialogTitle>
                        <DialogDescription className="text-xs text-slate-500">
                            Parties opted-in for the daily 23:45 IST ledger email but with a missing or invalid address. Add a valid email for each — one save updates them all so the next nightly run reaches them.
                        </DialogDescription>
                    </DialogHeader>
                    {fixHealth && (
                        <div className="grid grid-cols-4 gap-2 text-xs">
                            <div className="rounded border border-emerald-200 bg-emerald-50 p-2">
                                <div className="text-[10px] uppercase tracking-wider text-emerald-700">OK</div>
                                <div className="font-mono text-lg font-bold text-emerald-800" data-testid="fix-count-ok">{fixHealth.counts?.ok || 0}</div>
                            </div>
                            <div className="rounded border border-amber-200 bg-amber-50 p-2">
                                <div className="text-[10px] uppercase tracking-wider text-amber-700">Missing</div>
                                <div className="font-mono text-lg font-bold text-amber-800" data-testid="fix-count-missing">{fixHealth.counts?.missing || 0}</div>
                            </div>
                            <div className="rounded border border-rose-200 bg-rose-50 p-2">
                                <div className="text-[10px] uppercase tracking-wider text-rose-700">Invalid</div>
                                <div className="font-mono text-lg font-bold text-rose-800" data-testid="fix-count-invalid">{fixHealth.counts?.invalid || 0}</div>
                            </div>
                            <div className="rounded border border-slate-200 bg-slate-50 p-2">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500">Opted out</div>
                                <div className="font-mono text-lg font-bold text-slate-700" data-testid="fix-count-off">{fixHealth.counts?.off || 0}</div>
                            </div>
                        </div>
                    )}
                    <div className="max-h-[420px] overflow-y-auto rounded border border-slate-200">
                        {fixHealth?.parties?.filter((p) => p.status !== "ok").length === 0 ? (
                            <div className="p-6 text-center text-sm text-slate-500" data-testid="fix-emails-clean">
                                Every opted-in party has a valid email address. You're good.
                            </div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                                        <th className="px-3 py-2">Party</th>
                                        <th className="px-3 py-2">Status</th>
                                        <th className="px-3 py-2">New email</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {fixHealth?.parties?.filter((p) => p.status !== "ok").map((p) => (
                                        <tr key={p.id} data-testid={`fix-row-${p.id}`}>
                                            <td className="px-3 py-2">
                                                <div className="font-medium text-slate-900">{p.name}</div>
                                                {p.phone && <div className="mt-0.5 font-mono text-[10px] text-slate-500">{p.phone}</div>}
                                            </td>
                                            <td className="px-3 py-2">
                                                {p.status === "missing" ? (
                                                    <span className="inline-flex items-center rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800">Missing</span>
                                                ) : (
                                                    <div>
                                                        <span className="inline-flex items-center rounded-sm bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rose-800">Invalid</span>
                                                        <div className="mt-0.5 font-mono text-[10px] text-slate-500 line-through">{p.effective}</div>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2">
                                                <Input
                                                    type="email"
                                                    data-testid={`fix-input-${p.id}`}
                                                    value={fixDraft[p.id] ?? ""}
                                                    onChange={(e) => setFixDraft({ ...fixDraft, [p.id]: e.target.value })}
                                                    placeholder="party@example.com"
                                                    className="h-8 bg-white text-sm"
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setFixOpen(false)}>Cancel</Button>
                        <Button
                            data-testid="fix-emails-save-btn"
                            onClick={saveFixEmails}
                            disabled={fixBusy || !fixHealth?.parties?.some((p) => p.status !== "ok")}
                            className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                        >
                            {fixBusy ? "Saving…" : "Save all"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Import CSV Dialog */}
            <Dialog open={importOpen} onOpenChange={setImportOpen}>                <DialogContent data-testid="import-parties-dialog" className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <UploadSimple size={20} weight="fill" className="text-blue-600" /> Bulk import parties from CSV
                        </DialogTitle>
                        <DialogDescription className="text-xs text-slate-500">
                            Columns: <span className="font-mono text-[11px]">name</span> (required), contact_person, phone, email, address, gst_no, opening_balance, notes, active. Duplicate names are skipped.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <button
                            type="button"
                            data-testid="import-download-template"
                            onClick={downloadTemplate}
                            className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
                        >
                            <Download size={14} /> Download CSV template
                        </button>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Choose CSV file</Label>
                            <Input
                                data-testid="import-file-input"
                                type="file"
                                accept=".csv,text/csv"
                                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                                className="mt-1"
                            />
                        </div>
                        {importResult && (
                            <div className="rounded-md border border-slate-200 bg-white p-3 text-sm" data-testid="import-result">
                                <div className="mb-1 flex items-center gap-3">
                                    <span className="text-emerald-700 font-semibold">{importResult.created?.length || 0} created</span>
                                    <span className="text-amber-700">{importResult.skipped?.length || 0} skipped</span>
                                    <span className="text-rose-700">{importResult.errors?.length || 0} errors</span>
                                </div>
                                {(importResult.skipped?.length || 0) > 0 && (
                                    <div className="mt-1 text-xs text-amber-800">
                                        Skipped (duplicates): {importResult.skipped.slice(0, 5).map((s) => s.name).join(", ")}
                                        {importResult.skipped.length > 5 ? ` … +${importResult.skipped.length - 5} more` : ""}
                                    </div>
                                )}
                                {(importResult.errors?.length || 0) > 0 && (
                                    <div className="mt-1 text-xs text-rose-800">
                                        Errors on rows: {importResult.errors.slice(0, 5).map((e) => `#${e.row} (${e.reason})`).join(", ")}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setImportOpen(false)}>Close</Button>
                        <Button
                            data-testid="import-submit-btn"
                            onClick={importCsv}
                            disabled={!importFile || importBusy}
                            className="bg-blue-600 text-white hover:bg-blue-700"
                        >
                            {importBusy ? "Importing…" : "Import"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {parties.length === 0 ? (
                <EmptyState title="No parties yet" description="Create your first party to start tracking collections."
                    action={<Button onClick={openCreate} className="mt-2 bg-blue-600 text-white hover:bg-blue-700">Add Party</Button>} />
            ) : (
                <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="parties-table">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                                    <th className="px-5 py-3 font-medium">Party</th>
                                    <th className="px-5 py-3 font-medium">Contact</th>
                                    <th className="px-5 py-3 text-right font-medium">Opening</th>
                                    <th className="px-5 py-3 font-medium">Status</th>
                                    <th className="px-5 py-3 text-right font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parties.map((p) => (
                                    <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                                        <td className="px-5 py-3">
                                            <div className="font-medium text-slate-900 flex items-center gap-1.5">
                                                {p.name}
                                                {p.last_email && (p.last_email.status === "failed" || p.last_email.status === "bounced") && (
                                                    <a
                                                        href={`/admin/email-log?party=${encodeURIComponent(p.name)}`}
                                                        onClick={(e) => { e.preventDefault(); navigate(`/admin/email-log?party=${encodeURIComponent(p.name)}`); }}
                                                        data-testid={`party-email-fail-${p.id}`}
                                                        className="inline-flex items-center gap-1 rounded border border-rose-300 bg-rose-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-rose-700 hover:bg-rose-100"
                                                        title={p.last_email.error || "Latest email did not deliver — open Email Log to retry"}
                                                    >
                                                        <span aria-hidden>!</span> {p.last_email.status === "bounced" ? "Bounced" : "Failed"}
                                                    </a>
                                                )}
                                            </div>
                                            <div className="text-xs text-slate-500">{p.address}</div>
                                        </td>
                                        <td className="px-5 py-3 text-slate-600">
                                            <div>{p.contact_person}</div>
                                            <div className="text-xs text-slate-500">{p.phone}</div>
                                        </td>
                                        <td className="px-5 py-3 text-right tabular-nums text-slate-900">{fmt(p.opening_balance)}</td>
                                        <td className="px-5 py-3"><StatusBadge status={p.active ? "active" : "inactive"} /></td>
                                        <td className="px-5 py-3">
                                            <div className="flex justify-end">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <button
                                                            data-testid={`party-actions-${p.id}`}
                                                            aria-label="Party actions"
                                                            className="rounded-md border border-slate-200 bg-white p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
                                                        >
                                                            <DotsThreeVertical size={18} weight="bold" />
                                                        </button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-56">
                                                        {userForParty(p.id) ? (
                                                            <DropdownMenuItem
                                                                data-testid={`menu-remove-user-${p.id}`}
                                                                onClick={() => removeUser(p)}
                                                                className="cursor-pointer text-amber-700 focus:text-amber-800"
                                                            >
                                                                <UserMinus size={16} className="mr-2" />
                                                                Remove portal login
                                                            </DropdownMenuItem>
                                                        ) : canCreateLogins ? (
                                                            <DropdownMenuItem
                                                                data-testid={`menu-add-user-${p.id}`}
                                                                onClick={() => openAddUser(p)}
                                                                className="cursor-pointer"
                                                            >
                                                                <UserPlus size={16} className="mr-2 text-slate-500" />
                                                                Create portal login
                                                            </DropdownMenuItem>
                                                        ) : (
                                                            <DropdownMenuItem
                                                                data-testid={`menu-add-user-locked-${p.id}`}
                                                                disabled
                                                                className="cursor-not-allowed opacity-50"
                                                                title="Only the Super Admin (or admins with permission) can create portal logins"
                                                            >
                                                                <Lock size={16} className="mr-2 text-slate-400" />
                                                                Create portal login
                                                            </DropdownMenuItem>
                                                        )}
                                                        <DropdownMenuItem
                                                            data-testid={`menu-manage-agents-${p.id}`}
                                                            onClick={() => setAgentsForParty(p)}
                                                            className="cursor-pointer"
                                                        >
                                                            <UsersThree size={16} className="mr-2 text-indigo-600" />
                                                            Manage Agents
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            data-testid={`menu-edit-party-${p.id}`}
                                                            onClick={() => openEdit(p)}
                                                            className="cursor-pointer"
                                                        >
                                                            <PencilSimple size={16} className="mr-2 text-slate-500" />
                                                            Edit party
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        {canDelete && (
                                                        <DropdownMenuItem
                                                            data-testid={`menu-delete-party-${p.id}`}
                                                            onClick={() => remove(p)}
                                                            className="cursor-pointer text-rose-600 focus:text-rose-700"
                                                        >
                                                            <Trash size={16} className="mr-2" />
                                                            Delete party
                                                        </DropdownMenuItem>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Create Portal Login dialog */}
            <ManageAgentsDialog
                open={!!agentsForParty}
                onOpenChange={(v) => !v && setAgentsForParty(null)}
                party={agentsForParty}
            />
            <Dialog open={userOpen} onOpenChange={setUserOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">Create Customer Portal Login</DialogTitle>
                        <DialogDescription className="text-xs text-slate-500">Give the party a way to sign in — email, mobile, or both.</DialogDescription>
                    </DialogHeader>
                    <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                        <div className="flex items-start gap-2">
                            <DeviceMobile size={16} className="mt-0.5 shrink-0" weight="fill" />
                            <div>
                                Provide <span className="font-semibold">email, mobile, or both</span>. The party can sign in with either at <span className="font-mono">{PORTAL_URL}</span>.
                            </div>
                        </div>
                    </div>
                    <form onSubmit={saveUser} className="space-y-4">
                        <Field label="Full Name *" required value={userForm.name} onChange={(v) => setUserForm({ ...userForm, name: v })} testId="user-name" />
                        <Field label="Mobile (10 digits)" type="tel" value={userForm.phone} onChange={(v) => setUserForm({ ...userForm, phone: v.replace(/\D/g, "").slice(0, 10) })} testId="user-phone"
                            hint="Required if email is not provided. Party can log in with just this number." />
                        <Field label="Email" type="email" value={userForm.email} onChange={(v) => setUserForm({ ...userForm, email: v })} testId="user-email"
                            hint="Optional if mobile is provided." />
                        <Field label="Password *" required value={userForm.password} onChange={(v) => setUserForm({ ...userForm, password: v })} testId="user-password"
                            hint="Share this with the party — they can change it later." />
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setUserOpen(false)}>Skip</Button>
                            <Button
                                type="submit"
                                data-testid="save-user-btn"
                                disabled={savingUser}
                                className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {savingUser ? "Creating…" : "Create Login"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Share credentials dialog */}
            <Dialog open={shareOpen} onOpenChange={setShareOpen}>
                <DialogContent data-testid="share-credentials-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <CheckCircle size={22} weight="fill" className="text-emerald-600" />
                            Portal login created
                        </DialogTitle>
                        <DialogDescription className="text-xs text-slate-500">Share these credentials with the party. They can change the password after signing in.</DialogDescription>
                    </DialogHeader>
                    {shareInfo && (
                        <div className="space-y-4">
                            <p className="text-sm text-slate-600">
                                Share these credentials with <span className="font-medium text-slate-900">{shareInfo.partyName}</span> so they can sign in from their phone and upload payment slips.
                            </p>
                            <div className="space-y-2.5 rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                                <CredRow label="Portal URL" value={PORTAL_URL} onCopy={() => copyText(PORTAL_URL)} testId="cred-url" />
                                {shareInfo.email && (
                                    <CredRow label="Email" value={shareInfo.email} onCopy={() => copyText(shareInfo.email)} testId="cred-email" />
                                )}
                                {shareInfo.phone && (
                                    <CredRow label="Mobile" value={shareInfo.phone} onCopy={() => copyText(shareInfo.phone)} testId="cred-phone" />
                                )}
                                <CredRow label="Password" value={shareInfo.password} onCopy={() => copyText(shareInfo.password)} testId="cred-password" />
                            </div>
                            <Button
                                onClick={openWhatsApp}
                                data-testid="whatsapp-send-btn"
                                className="w-full bg-[#25D366] text-white hover:bg-[#1ea856]"
                            >
                                <WhatsappLogo size={18} weight="fill" className="mr-2" />
                                {shareInfo.partyPhone ? `Send via WhatsApp to ${shareInfo.partyPhone}` : "Open WhatsApp"}
                            </Button>
                            <Button
                                onClick={copyAll}
                                data-testid="copy-all-btn"
                                variant="outline"
                                className="w-full"
                            >
                                <Copy size={16} className="mr-2" /> Copy message instead
                            </Button>
                            <p className="text-xs text-slate-500">
                                Tip: WhatsApp opens with the message pre-typed. Just tap Send. The party opens the URL on their phone, signs in, and uploads a screenshot — details are extracted automatically.
                            </p>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setShareOpen(false)} data-testid="close-share-btn">Done</Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", required, testId, className = "", hint }) {
    return (
        <div className={className}>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</Label>
            <Input data-testid={testId} className="mt-2" type={type} required={required} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
            {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
        </div>
    );
}

/**
 * v1.23.0 — Email schedule editor for one party.
 * Lets admin choose Off / Daily / Weekly / Monthly + a time-of-day + which email
 * address to send to (falls back to the party's own email field).
 */
function EmailScheduleBlock({ value, partyEmail, onChange, editingId }) {
    const s = value || { frequency: "off", time_hhmm: "23:45", weekday: 1, day_of_month: 1, recipient: "" };
    const set = (patch) => onChange({ ...s, ...patch });
    const [sending, setSending] = React.useState(false);

    const sendTest = async () => {
        if (!editingId) { toast.error("Save the party first, then click 'Send now' to test."); return; }
        const to = s.recipient || partyEmail;
        if (!to) { toast.error("Add a recipient email or the party's email first."); return; }
        setSending(true);
        try {
            const { data } = await api.post(`/parties/${editingId}/email-ledger`, { to });
            toast.success(`Sent to ${data.sent_to}`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSending(false); }
    };

    const active = s.frequency !== "off";
    return (
        <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-4" data-testid="party-email-schedule">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-800">Auto-email statement</div>
                    <div className="mt-0.5 text-xs text-slate-500">Sends the party's ledger PDF + today's day-progress to their inbox on a schedule. Default fire time: <b>23:45 IST</b>. Admin also gets every party's PDF at 00:05 IST daily.</div>
                </div>
                <Switch checked={active} data-testid="schedule-enabled" onCheckedChange={(v) => set({ frequency: v ? "daily" : "off" })} />
            </div>
            {active && (
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                        <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Frequency</Label>
                        <select
                            data-testid="schedule-frequency"
                            value={s.frequency}
                            onChange={(e) => set({ frequency: e.target.value })}
                            className="mt-1 block h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                        >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                        </select>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Time of day (IST)</Label>
                        <Input
                            type="time"
                            value={s.time_hhmm}
                            data-testid="schedule-time"
                            onChange={(e) => set({ time_hhmm: e.target.value })}
                            className="mt-1 h-9"
                        />
                    </div>
                    {s.frequency === "weekly" && (
                        <div>
                            <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Weekday</Label>
                            <select
                                data-testid="schedule-weekday"
                                value={s.weekday}
                                onChange={(e) => set({ weekday: Number(e.target.value) })}
                                className="mt-1 block h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                            >
                                {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => (
                                    <option key={d} value={i}>{d}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {s.frequency === "monthly" && (
                        <div>
                            <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Day of month (1-28)</Label>
                            <Input
                                type="number" min={1} max={28}
                                value={s.day_of_month}
                                data-testid="schedule-day-of-month"
                                onChange={(e) => set({ day_of_month: Math.max(1, Math.min(28, Number(e.target.value) || 1)) })}
                                className="mt-1 h-9"
                            />
                        </div>
                    )}
                    <div className="sm:col-span-2">
                        <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
                            Recipient email {partyEmail ? <span className="text-slate-400 normal-case">(defaults to <span className="font-mono">{partyEmail}</span>)</span> : null}
                        </Label>
                        <Input
                            type="email"
                            placeholder={partyEmail || "party@example.com"}
                            value={s.recipient}
                            data-testid="schedule-recipient"
                            onChange={(e) => set({ recipient: e.target.value })}
                            className="mt-1 h-9"
                        />
                    </div>
                </div>
            )}
            <div className="mt-3 flex items-center justify-end gap-2 border-t border-blue-100 pt-3">
                <span className="mr-auto text-[11px] text-slate-500">
                    Subject line: <span className="font-mono">Ledger of {"{party}"} till dated {new Date(Date.now() - 864e5).toLocaleDateString("en-GB")}</span>
                </span>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!editingId || sending}
                    data-testid="schedule-send-now"
                    onClick={sendTest}
                    title={editingId ? "Send the ledger PDF right now to test delivery" : "Save the party first, then use Send now to test"}
                    className="h-8 border-blue-300 text-blue-800 hover:bg-blue-100 disabled:opacity-50"
                >
                    {sending ? "Sending…" : "Send now (test)"}
                </Button>
            </div>
        </div>
    );
}


function IconBtn({ children, onClick, title, testId }) {
    return (
        <button
            type="button"
            title={title}
            data-testid={testId}
            onClick={onClick}
            className="rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
        >
            {children}
        </button>
    );
}

function CredRow({ label, value, onCopy, testId }) {
    return (
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid={testId}>
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</div>
            <div className="mt-1.5 flex items-start gap-2">
                <div
                    className="min-w-0 flex-1 break-all rounded border border-slate-100 bg-slate-50 px-2.5 py-2 font-mono text-sm text-slate-800"
                    data-testid={`${testId}-value`}
                >
                    {value}
                </div>
                <button
                    type="button"
                    onClick={onCopy}
                    data-testid={`${testId}-copy`}
                    className="mt-0.5 flex shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-600 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                    title="Copy"
                >
                    <Copy size={14} />
                    <span className="hidden sm:inline">Copy</span>
                </button>
            </div>
        </div>
    );
}
